import { saveAs } from '@progress/kendo-file-saver';

/**
 * Per diem packet download helpers (US 35203). The packet endpoints (`POST /api/e2e/perdiempacket/*`) answer
 * with responseType blob, so the RA Per Diem Audit dashboard and the Sparta dispatch Per Diem Docs dialog both
 * resolve the file name, save the bytes and read error reasons the same way.
 */
export interface PerDiemPacketResponse {
  headers: { get(name: string): string | null };
  body: Blob | null;
}

/** The server's file name: `ups-file-name`, then the Content-Disposition filename, then the fallback. */
export function perDiemPacketFileName(
  response: PerDiemPacketResponse,
  fallbackFileName: string
): string {
  return (
    response.headers.get('ups-file-name') ??
    response.headers
      .get('Content-Disposition')
      ?.split(';')[1]
      ?.split('=')[1]
      ?.replace(/"/g, '') ??
    fallbackFileName
  );
}

/** The employees endpoint returns a PDF for one employee and a zip for several. */
export function employeePacketFallbackFileName(
  response: PerDiemPacketResponse
): string {
  const contentType =
    response.headers.get('Content-Type') ?? response.body?.type ?? '';
  return contentType.includes('application/zip')
    ? 'PerDiemPackets.zip'
    : 'PerDiemPacket.pdf';
}

/** Saves the packet bytes under the server's file name. */
export function savePerDiemPacket(
  response: PerDiemPacketResponse,
  fallbackFileName: string
): void {
  saveAs(response.body, perDiemPacketFileName(response, fallbackFileName));
}

/**
 * A 404's JSON body ({ Message, Flags: string[] }) arrives as a Blob. Reads it back so the user sees why no
 * packet was built (missing documents, foreign company job), with up to three flags.
 */
export async function perDiemPacketErrorMessage(
  error: unknown,
  fallbackMessage: string
): Promise<string> {
  const body = (error as { error?: unknown })?.error;
  if (!(body instanceof Blob)) {
    return fallbackMessage;
  }
  try {
    const parsed = JSON.parse(await body.text());
    if (!parsed?.Message) {
      return fallbackMessage;
    }
    const flags = Array.isArray(parsed.Flags)
      ? parsed.Flags.slice(0, 3).join('; ')
      : '';
    return flags ? `${parsed.Message} (${flags})` : parsed.Message;
  } catch {
    return fallbackMessage;
  }
}
